import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, resolveImageUrl } from "../lib/api";
import ProductCard from "../components/ProductCard";
import BatchModeToggle from "../components/BatchModeToggle";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const CAT_META = {
  flower: { title: "Flower", tag: "Hand-trimmed, small-batch" },
  wax: { title: "Wax", tag: "Rosin, shatter, sauce — by brand" },
  carts: { title: "Carts", tag: "Live resin & disposables — by brand" },
  edibles: { title: "Edibles", tag: "Gummies, bars, lozenges" },
};

const BRAND_CATEGORIES = new Set(["wax", "carts"]);

export default function Category() {
  const { cat } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const brandIdParam = searchParams.get("brand");
  const meta = CAT_META[cat] || { title: cat, tag: "" };
  const isBrandCategory = BRAND_CATEGORIES.has(cat);

  const [brands, setBrands] = useState([]);
  const [activeBrand, setActiveBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Reset brand selection when category changes
  useEffect(() => {
    setActiveBrand(null);
    setProducts([]);
  }, [cat]);

  // Load brands for carts/wax
  useEffect(() => {
    if (!isBrandCategory) {
      setBrands([]);
      return;
    }
    api.get("/brands", { params: { category: cat } }).then((r) => setBrands(r.data || []));
  }, [cat, isBrandCategory]);

  // Resolve active brand if URL has ?brand=
  useEffect(() => {
    if (!brandIdParam) {
      setActiveBrand(null);
      return;
    }
    api.get(`/brands/${brandIdParam}`).then((r) => setActiveBrand(r.data)).catch(() => setActiveBrand(null));
  }, [brandIdParam]);

  // Load products
  useEffect(() => {
    setLoading(true);
    const params = { category: cat };
    if (isBrandCategory && brandIdParam) params.brand_id = brandIdParam;
    // For non-brand categories, just load all products
    // For brand categories, only load when a brand is selected
    if (isBrandCategory && !brandIdParam) {
      setProducts([]);
      setLoading(false);
      return;
    }
    api.get("/products", { params })
      .then((r) => setProducts(r.data || []))
      .finally(() => setLoading(false));
  }, [cat, brandIdParam, isBrandCategory]);

  const clearBrand = () => {
    searchParams.delete("brand");
    setSearchParams(searchParams, { replace: true });
  };

  const selectBrand = (brandId) => {
    setSearchParams({ brand: brandId }, { replace: true });
  };

  return (
    <div className="pt-24 md:pt-28" data-testid={`category-page-${cat}`}>
      <section className="px-6 md:px-12 lg:px-24 pt-16 pb-10 border-b border-white/10">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gray-500 mb-5">
          <Link to="/" className="hover:text-white">Home</Link>
          <span>/</span>
          <span>Shop</span>
          <span>/</span>
          <Link
            to={`/shop/${cat}`}
            onClick={(e) => { if (!isBrandCategory) return; e.preventDefault(); clearBrand(); }}
            className={activeBrand ? "hover:text-white" : "text-[var(--hk-orange)]"}
          >
            {meta.title}
          </Link>
          {activeBrand && (
            <>
              <span>/</span>
              <span className="text-[var(--hk-orange)]">{activeBrand.name}</span>
            </>
          )}
        </div>
        <motion.h1
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="font-display text-6xl md:text-8xl lg:text-9xl font-bold uppercase tracking-tighter leading-[0.85]"
        >
          {activeBrand ? activeBrand.name : meta.title}
        </motion.h1>
        <p className="mt-4 text-gray-400 text-sm md:text-base">
          {activeBrand ? (activeBrand.description || `${meta.title} from ${activeBrand.name}`) : meta.tag}
        </p>

        {/* Only show batch toggle on the product view (not the brand grid) */}
        {(!isBrandCategory || activeBrand) && (
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <BatchModeToggle />
            <p className="text-xs text-gray-500 uppercase tracking-[0.3em]">
              Switch pricing — small batch retail or wholesale (min 5)
            </p>
          </div>
        )}

        {/* Back-to-brands button when viewing a brand */}
        {isBrandCategory && activeBrand && (
          <button
            onClick={clearBrand}
            data-testid="back-to-brands"
            className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gray-400 hover:text-white"
          >
            <ArrowLeft size={14} /> All brands
          </button>
        )}
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-12">
        {isBrandCategory && !activeBrand ? (
          <BrandGrid brands={brands} onSelect={selectBrand} category={cat} />
        ) : (
          <CatalogSection loading={loading} products={products} activeBrand={activeBrand} />
        )}
      </section>
    </div>
  );
}

function CatalogSection({ loading, products, activeBrand }) {
  if (loading) {
    return <div className="text-gray-500 font-mono-alt tracking-widest">LOADING DROP...</div>;
  }
  if (products.length === 0) {
    return (
      <div className="text-gray-500 py-20 text-center">
        {activeBrand ? `No products from ${activeBrand.name} yet.` : "No drops in this category yet."}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
      {products.map((p, i) => (
        <ProductCard key={p.id} product={p} index={i} />
      ))}
    </div>
  );
}

function BrandGrid({ brands, onSelect, category }) {
  if (brands.length === 0) {
    return (
      <div className="text-gray-500 py-20 text-center">
        <p className="font-display uppercase text-xl text-white mb-3">No brands yet</p>
        <p className="text-sm">
          The owner can add brands from the admin panel — products will be grouped under each brand here.
        </p>
      </div>
    );
  }
  return (
    <>
      <div className="flex items-end justify-between gap-6 mb-10">
        <div>
          <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-purple-text)] mb-2">
            / BRANDS · {category.toUpperCase()}
          </p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter">
            Pick a <span className="italic text-[var(--hk-orange)]">brand</span>
          </h2>
        </div>
        <p className="hidden md:block text-gray-400 text-sm max-w-xs">
          {brands.length} brand{brands.length === 1 ? "" : "s"} carrying {category}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {brands.map((b, i) => (
          <motion.button
            key={b.id}
            data-testid={`brand-tile-${b.id}`}
            onClick={() => onSelect(b.id)}
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: (i % 8) * 0.05 }}
            className="group relative border border-white/10 hover:border-[var(--hk-purple)]/60 bg-gradient-to-b from-white/[0.02] to-transparent overflow-hidden transition-all hover:-translate-y-1 text-left"
          >
            <div className="aspect-square overflow-hidden bg-black flex items-center justify-center">
              {b.logo ? (
                <img
                  src={resolveImageUrl(b.logo)}
                  alt={b.name}
                  className="w-3/4 h-3/4 object-contain opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
              ) : (
                <span className="font-display text-3xl uppercase tracking-tight text-white/40">
                  {b.name.slice(0, 2)}
                </span>
              )}
            </div>
            <div className="p-4 flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-display uppercase tracking-tight text-white truncate">{b.name}</p>
                {b.description && (
                  <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">{b.description}</p>
                )}
              </div>
              <ArrowRight size={14} className="text-[var(--hk-orange)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            </div>
          </motion.button>
        ))}
      </div>
    </>
  );
}
